import { useConversationPermissions } from '../../../../shared/hooks/use-permissions';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { chatListSetter, chatRowSetter, eligibleChatMessagesSetter, getCountFromStatus } from '../states/chat';
import {
  ChatStatuses,
  ConversationTag,
  EligibleMessageTypes,
  InternalNote,
  ReminderCreatedPayload,
  ReminderTriggeredPayload,
  UpdateNoteMessageEventPayload,
  ReminderStatusUpdatePayload,
  ScheduledChatMessageSocketPayload,
} from '../typings';
import { getNewMessageSocket } from '../../../../infra/sockets';
import {
  Message,
  MessageOrigin,
  SystemMessage,
  CommercePrivacyAccessData,
  NoteMessage,
  ActionType,
  ReminderMessage,
  REMINDER_MESSAGE_STATUS,
  ReminderTriggerMessage,
  CallSummaryStatus,
  CallMessage,
} from '../typings/message-types';
import ChatCache, { deleteMessageFromCache, sendMessageToCache } from '../states/cache';
import {
  AICallEventPayload,
  CallSummaryEventReasonTypes,
  ChatFiltersEventPayload,
  ChatFiltersEventReasonTypes,
  ChatType,
  IndividualChatRowData,
  NewMessageChatRow,
  TextMessageData,
} from '../components/chat-list-section/chat-row/typings';
import reportToSentry from '../../../../shared/utils/report-to-sentry';
import { getError } from '../../../../shared/utils/get-error';
import chatRowDataHelper from '../../../../shared/utils/chat-row-data-helper';
import { CHAT_UPDATE_TYPE, ChatUnreadCountBroadcast, MessageStatus } from '../typings/chat';
import { getChatUpdatesUseCase } from '../use-cases/get-chat-updates.use-case';
import { useChatListFilter } from './use-chat-list-filter';
import { useSetOpenTestBroadcastModal } from '../../../../shared/hooks/use-open-test-broadcast-modal';
import { getCurrentUserId } from '../../../../infra/firebase/init';
import useSocket from '../../../../shared/hooks/use-socket';
import { customerDetailAtomFamily, internalNotesListAtomFamily } from '../states/customer-details';
import { selectedChatAtom, selectedChatIdAtom } from '../states/selected-chat';
import { FiltersState, chatFilterStatusCountsAtom, chatListAtom } from '../states/chat-list';
import { getRandomNumberBetweenRange } from '../../../../shared/utils/number';
import { useReducerAtom } from 'jotai/utils';
import { socketConnectionAtom, socketConnectionReducer } from '../../../../shared/states/socket-connection';
import { SocketConnectionReducerActionTypes, SocketName } from '../../../../shared/typings/socket-connection';
import usePushNotification from '../../../../shared/utils/show-push-notification';
import { FCMNotificationType } from '../../../../shared/typings';
import { getUserChannelData } from '../../../login/v1/utility';
import { MessageTypesEnum } from '../typings/message-types';
import { conversationSocketEventAtom, socketEventReceivedAtom } from '../states/message';
import { internalNotesCountAtom } from '../components/customer-details/state';
import { isFilterAIAwaitingResponseEnabled } from '../components/chat-list-section/ai-filtering/state';
import { reminderTriggerAtom } from '../states/reminder';
import { callBulkSummaryProgressAtom } from '../states/ai-call-bulk-summary';
import useReminderFallbackPoll from './use-reminder-fallback-poll';
import { CallSummaryProgressState, CallSummaryProgressStatus } from '../typings/ai-call-summary';
import { teamMembersPrivateNotesAtom } from '../states/team-members';
import { formatNoteMentions } from '../../../../shared/utils/text-helper';
import { mentionRegex } from '../components/chat-input/constants';
import { scheduledMessagesAtomFamily, scheduledMessagesSortOptionAtom } from '../states/scheduled-messages';
import { ScheduledMessage } from '../requests/get-scheduled-messages';
import { scheduledMessageBannerAtom } from '../components/chat-input/state';

export const useConversationSocket = () => {
  const currentUserId = getCurrentUserId();
  const orgName = getUserChannelData()?.name ?? 'Doubletick';
  const setSendTestBroadcastModal = useSetOpenTestBroadcastModal();
  const [_, dispatch] = useReducerAtom(socketConnectionAtom, socketConnectionReducer);

  const updateCache = useSetAtom(sendMessageToCache);
  const deleteCache = useSetAtom(deleteMessageFromCache);
  const setChatList = useSetAtom(chatListSetter);
  const setEligibleMessageTypes = useSetAtom(eligibleChatMessagesSetter);
  const setChatRow = useSetAtom(chatRowSetter);
  const setSelectedChatId = useSetAtom(selectedChatIdAtom);
  const { getConversationPermissions } = useConversationPermissions();
  const { isFilterSelectRef } = useChatListFilter();
  const customFieldConditions = useAtomValue(chatListAtom).customFieldConditions;
  const hasCustomFieldFilterRef = useRef((customFieldConditions ?? []).length > 0);
  hasCustomFieldFilterRef.current = (customFieldConditions ?? []).length > 0;
  const updateChatListSectionViaSocketUpdates = useCallback(
    async (lastUpdateTimestamp: number) => {
      try {
        const socketUpdates = await getChatUpdatesUseCase(lastUpdateTimestamp);
        if (socketUpdates) {
          setChatList({
            type: CHAT_UPDATE_TYPE.API_UPDATES,
            updates: socketUpdates,
          });
        }
      } catch (error) {
        reportToSentry(getError(error));
      }
    },
    [setChatList]
  );
  const selectedChat = useAtomValue(selectedChatAtom);
  const phone = selectedChat?.chatType === ChatType.INDIVIDUAL ? selectedChat.phoneNumber : '';
  const integrationWabaNumber = selectedChat?.integrationWabaNumber || '';
  const customerDetailAtom = useMemo(() => {
    return customerDetailAtomFamily({
      id: `${integrationWabaNumber}::${phone}`,
    });
  }, [integrationWabaNumber, phone]);

  const { showPushNotification } = usePushNotification();
  const setCustomerDetails = useSetAtom(customerDetailAtom);
  const setChatFilterStatusCounts = useSetAtom(chatFilterStatusCountsAtom);
  const setInternalNotesList = useSetAtom(internalNotesListAtomFamily({ id: selectedChat?.id || '' }));
  const setInternalNotesCount = useSetAtom(internalNotesCountAtom);
  const setScheduledMessages = useSetAtom(scheduledMessagesAtomFamily(selectedChat?.id ?? ''));
  const setScheduledBanner = useSetAtom(scheduledMessageBannerAtom);
  const scheduledSortOption = useAtomValue(scheduledMessagesSortOptionAtom);
  const setSocketEventReceived = useSetAtom(socketEventReceivedAtom);
  const setConversationSocketEvent = useSetAtom(conversationSocketEventAtom);
  const isAiAwaitingReplyEnabled = useAtomValue(isFilterAIAwaitingResponseEnabled);
  const state = useAtomValue(chatListAtom);
  const { status, conversationView } = state;
  const [reminderTrigger, setReminderTrigger] = useAtom(reminderTriggerAtom);
  const { startPolling, stopPolling } = useReminderFallbackPoll();
  const setCallBulkSummaryProgress = useSetAtom(callBulkSummaryProgressAtom);
  const teamMembersPrivateNotesData = useAtomValue(teamMembersPrivateNotesAtom);

  const { socket: conversationSocket } = useSocket(
    getNewMessageSocket,
    updateChatListSectionViaSocketUpdates,
    true,
    (status) =>
      dispatch({
        type: SocketConnectionReducerActionTypes.CONNECTION,
        data: {
          name: SocketName.CONVERSATION,
          status,
        },
      }),
    {
      enabled: true,
      start: startPolling,
      stop: stopPolling,
    },
    {
      doNotDisconnectOnUnmount: true,
      reconnectOnFocus: true,
      reconnectOnVisibility: true,
      // keep default maxIdleHoursBeforeForcedReconnect (6h) for safety
    }
  );

  const updateChatReadCount = useCallback(
    (id: string, unreadCount: number) => {
      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        reOrder: false,
        data: {
          id,
          unreadCount,
        },
      });
    },
    [setChatRow]
  );

  const updateChatReadCountRef = useRef(updateChatReadCount);
  updateChatReadCountRef.current = updateChatReadCount;

  const markDone = useCallback(
    (id: string, isDone?: boolean) => {
      if (isDone) {
        setChatList({
          type: CHAT_UPDATE_TYPE.PARTIAL_UPDATE,
          data: {
            id,
            isDone,
          },
        });
        return;
      }
      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        reOrder: false,
        data: {
          id,
          isDone,
        },
      });
      setChatList({
        type: CHAT_UPDATE_TYPE.PARTIAL_UPDATE,
        data: {
          id,
          isDone,
        },
      });
    },
    [setChatRow, setChatList]
  );

  const onIsDoneEvent = useCallback(
    (payload: { chatId: string; isDone: boolean; validUserIds?: string[] }, checkForValidUserIds?: boolean) => {
      setSocketEventReceived(true);
      const { chatId, isDone, validUserIds } = payload;
      if (checkForValidUserIds && currentUserId && !validUserIds?.includes(currentUserId)) return;
      markDone(chatId, isDone);
    },
    [markDone]
  );

  const onMarkDoneEvent = useCallback(
    ({ chatId, unreadCount, validUserIds }: ChatUnreadCountBroadcast) => {
      if (currentUserId && !validUserIds?.includes(currentUserId)) return;
      updateChatReadCountRef.current(chatId, unreadCount);
    },
    [currentUserId]
  );

  const onChatSlaStatusEvent = useCallback(
    (payload: {
      chatId: string;
      isSlaActive?: boolean;
      slaDueDate?: string;
      slaBreachLevel?: number;
      validUserIds?: string[];
    }) => {
      setSocketEventReceived(true);
      const { chatId, isSlaActive = false, slaDueDate = '', slaBreachLevel } = payload;

      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        data: {
          id: chatId,
          isSlaActive,
          slaDueDate,
          slaBreachLevel,
        },
        skipNew: true,
      });
    },
    [setChatRow]
  );

  const onChatSlaStatusEventForAddChat = useCallback(
    (payload: {
      chatId: string;
      isSlaActive?: boolean;
      slaDueDate?: string;
      slaBreachLevel?: number;
      validUserIds?: string[];
    }) => {
      const { chatId, isSlaActive = false, slaDueDate = '', slaBreachLevel, validUserIds } = payload;

      if (currentUserId && !validUserIds?.includes(currentUserId)) return;

      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        data: {
          id: chatId,
          isSlaActive,
          slaDueDate,
          slaBreachLevel,
        },
      });
    },
    [setChatRow]
  );

  const onPrivacyUpdateEvent = useCallback(
    (payload: CommercePrivacyAccessData) => {
      setSocketEventReceived(true);
      const message: Message = {
        id: payload.messageId,
        message: payload.message,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        messageTime: payload.messageTime,
        senderId: payload.senderId,
        messageOriginType: payload.senderType,
        messageMetadata: null,
        readCount: 0,
        senderUser: payload.chat.senderUser,
        templateId: null,
        totalCount: 1,
        errorMessage: '',
        quickActionButtonStats: [],
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
      };
      updateCache({
        chatId: payload.chatId,
        message,
        partial: false,
        queue: payload.senderType === MessageOrigin.USER,
        fromSocket: true,
      });
    },
    [updateCache]
  );

  const assignTagsEventHandler = useCallback(
    (payload: { chatId: string; tags: ConversationTag[]; validUserIds?: string[] }) => {
      const { chatId, tags, validUserIds } = payload;

      if (currentUserId && !validUserIds?.includes(currentUserId)) return;

      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        reOrder: false,
        data: {
          id: chatId,
          tags,
        },
        skipNew: true,
      });
    },
    [setChatRow]
  );

  const isAIAwaitingReplyActive = isAiAwaitingReplyEnabled && status === ChatStatuses.WAITING_FOR_RESPONSE;

  const aiWaitingForReplyChatIdSet = useRef<Set<string>>(new Set<string>());

  // clear the tracking set whenever the AI awaiting reply filter is toggled
  useEffect(() => {
    aiWaitingForReplyChatIdSet.current.clear();
  }, [isAiAwaitingReplyEnabled]);

  const onNewMessageForAddChatSocketEventHandler = useCallback(
    (payload: NewMessageChatRow, eventReason: ChatFiltersEventReasonTypes) => {
      const isReminderMessage = typeof payload['reminderId'] === 'string' && payload['reminderId'].length > 0;
      if (currentUserId && !payload.validUserIds?.includes(currentUserId)) return;

      // when AI awaiting reply filter is active and we receive a "waiting for response" event,
      // add this chat ID to our tracking set so we know it's in the chat list
      if (isAIAwaitingReplyActive && eventReason === ChatFiltersEventReasonTypes.WAITING_FOR_RESPONSE) {
        aiWaitingForReplyChatIdSet.current.add(payload.chatId);
      }

      // when AI awaiting reply filter is active and we receive a "new message" event,
      // only process it if the chat is already in our chat list.
      // This filters out new messages from chats that aren't in the waiting state,
      // ensuring the filter only shows messages from chats that are actually waiting for a response
      if (isAIAwaitingReplyActive && eventReason === ChatFiltersEventReasonTypes.NEW_MESSAGE) {
        if (!aiWaitingForReplyChatIdSet.current.has(payload.chatId)) {
          return;
        }
      }

      // Guard by current view and chat type:
      // In Groups view, ignore non-WA_GROUP chats so individual chats
      // don't appear or reorder in the Groups-only list.
      if (conversationView === 'groups' && payload.chat.chatType !== ChatType.WA_GROUP) {
        return;
      }

      const isSystemMessage =
        payload.message?.messageType === MessageTypesEnum.SYSTEM ||
        payload.message?.messageType === MessageTypesEnum.SYSTEM_LOG;

      if (!isSystemMessage || isReminderMessage) {
        const isReactionMessage = !!payload.message?.agentReaction || !!payload.message?.reaction;
        // do not update lastMessageTime for reaction messages
        if (isReactionMessage) {
          let data = chatRowDataHelper(payload);
          if (data.lastMessageTime) {
            delete data.lastMessageTime;
          }
          setChatRow({
            type: CHAT_UPDATE_TYPE.UPDATE,
            partial: true,
            reOrder: false,
            data: data,
          });
        } else {
          setChatRow({
            type: CHAT_UPDATE_TYPE.UPDATE,
            partial: false,
            skipNew: hasCustomFieldFilterRef.current,
            data: chatRowDataHelper(payload),
            //sockets are not returning all chat row data like tags etc
            useOldValue: true,
            reOrder: !isSystemMessage || isReminderMessage,
          });
        }
      }
    },
    [
      getConversationPermissions,
      currentUserId,
      setChatRow,
      isFilterSelectRef,
      updateCache,
      setSendTestBroadcastModal,
      setEligibleMessageTypes,
      selectedChat,
      isAiAwaitingReplyEnabled,
      status,
      conversationView,
    ]
  );

  const onNewMessageSocketEventHandler = useCallback(
    (payload: NewMessageChatRow) => {
      setSocketEventReceived(true);
      const permissions = getConversationPermissions(payload.chat.id, payload.chat.assignedUserId);
      if (currentUserId !== null && payload.chat.assignedUserId === currentUserId) {
        setSendTestBroadcastModal((prev) => (!prev.open ? prev : { open: false }));
      }
      //FIXME PERMISSION
      if (!permissions.canRead) {
        return;
      }

      // Check if this is a system log with call permission action types
      if (payload.message?.messageType === MessageTypesEnum.SYSTEM && selectedChat?.id === payload.chatId) {
        const actionType = payload.message?.actionType;
        if (
          actionType === ActionType.CALL_PERMISSION_REJECTED ||
          actionType === ActionType.CALL_PERMISSION_ACCEPTED ||
          actionType === ActionType.CALL_PERMISSION_ACCEPTED_PERMANENT
        ) {
          // Dispatch custom event for call permission changes
          window.dispatchEvent(
            new CustomEvent('call-permission-updated', {
              detail: {
                chatId: payload.chatId,
                phoneNumber: (payload.chat as IndividualChatRowData).phoneNumber,
                integrationWabaNumber: payload.chat.integrationWabaNumber,
                actionType,
              },
            })
          );
        }
      }

      if (payload.senderType === MessageOrigin.CUSTOMER) {
        if (selectedChat?.id !== payload.chatId) {
          const title =
            payload.chat.chatType !== ChatType.INDIVIDUAL
              ? payload.chat.name
              : payload.chat.name || payload.chat.phoneNumber;
          showPushNotification({
            messageId: payload.messageId,
            collapseKey: '',
            from: '',
            data: {
              chatId: payload.chatId,
              chatString: JSON.stringify(payload.chat),
              type: FCMNotificationType.MESSAGE,
              title: title,
              body: (payload.message as TextMessageData).text ?? 'You have a new message',
              orgId: payload.orgId,
              messageId: payload.messageId,
              senderType: payload.senderType,
            },
          });
        }
      }
      const message: Message = {
        id: payload.messageId,
        message: payload.message as SystemMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: payload.replyMessage ?? undefined,
        messageTime: payload.messageTime,
        senderId: payload.senderId,
        messageOriginType: payload.messageOriginType,
        messageMetadata: payload.messageMetadata,
        readCount: 0,
        senderUser: payload.chat.senderUser,
        templateId: null,
        totalCount: 1,
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
        showReadMore: !(selectedChat?.id === payload.chatId && payload.messageOriginType === MessageOrigin.CUSTOMER),
        ...getCountFromStatus(payload.status),
        errorMessage: '',
      };

      if (payload.senderType === MessageOrigin.CUSTOMER) {
        setEligibleMessageTypes({
          chatId: payload.chatId,
          eligibleTypes: [EligibleMessageTypes.ANY],
        });
      }

      updateCache({
        chatId: payload.chatId,
        message,
        partial: false,
        queue: payload.senderType === MessageOrigin.USER,
        fromSocket: true,
      });

      if (payload.chatId === selectedChat?.id && 'upcomingScheduledChatMessageTime' in payload) {
        const rawTime = payload.upcomingScheduledChatMessageTime;
        const timestamp = rawTime ? new Date(rawTime).getTime() : null;
        setScheduledBanner(timestamp ? { timestamp, timezone: payload.upcomingScheduledChatMessageTimezone } : null);
      }
    },
    [
      getConversationPermissions,
      currentUserId,
      setChatRow,
      isFilterSelectRef,
      updateCache,
      setSendTestBroadcastModal,
      setEligibleMessageTypes,
      selectedChat,
      showPushNotification,
      isAiAwaitingReplyEnabled,
      status,
      setScheduledBanner,
    ]
  );

  const onCustomerUpdatesEvent = useCallback(
    (payload: { isBlocked: boolean; customerId: string; name: string; orgId: string; phoneNumber: string }) => {
      setSocketEventReceived(true);
      const { isBlocked } = payload;

      setCustomerDetails((customerDetails) => {
        if (customerDetails.data) {
          return {
            ...customerDetails,
            data: {
              ...customerDetails.data,
              customerIsBlocked: isBlocked,
            },
          };
        } else return customerDetails;
      });
    },
    [setCustomerDetails]
  );

  const onChatFilterCountsEvent = useCallback(
    (payload: {
      type: keyof Pick<typeof FiltersState, 'ALL_CHATS' | 'MY_CHATS' | 'UNASSIGNED' | 'SLA_STATUS'>;
      status: ChatStatuses;
      count: string;
    }) => {
      const { type, status, count } = payload;
      setChatFilterStatusCounts((prev) => {
        const updatedChatFilterStatusCounts = [...(prev?.data ?? [])];
        const chatStatusFilterIndex = updatedChatFilterStatusCounts?.findIndex?.(
          (chatStatusFilterCount) => chatStatusFilterCount.type === type
        );
        if (chatStatusFilterIndex > -1) {
          const chatStatusFilterCountIndex = updatedChatFilterStatusCounts?.[
            chatStatusFilterIndex
          ]?.counts?.findIndex?.((chatStatusFilterCount) => chatStatusFilterCount.type === status);
          if (chatStatusFilterCountIndex > -1) {
            updatedChatFilterStatusCounts[chatStatusFilterIndex].counts[chatStatusFilterCountIndex].count = count;
          }
        }
        return { ...prev, data: updatedChatFilterStatusCounts };
      });
    },
    [setChatFilterStatusCounts]
  );

  const removeChat = useCallback(
    (chatId: string, validUserIds?: string[], key?: string) => {
      if (currentUserId && !validUserIds?.includes(currentUserId)) return;

      setChatRow({ type: CHAT_UPDATE_TYPE.DELETE, id: chatId });
    },
    [setChatRow, setSelectedChatId]
  );

  const onChatFiltersAddChatEventHandler = useCallback(
    (payload: ChatFiltersEventPayload) => {
      setSocketEventReceived(true);
      switch (payload.eventReason) {
        case ChatFiltersEventReasonTypes.WAITING_FOR_RESPONSE:
        case ChatFiltersEventReasonTypes.NEW_MESSAGE:
          onNewMessageForAddChatSocketEventHandler(payload.eventPayload, payload.eventReason);
          break;
        case ChatFiltersEventReasonTypes.IS_DONE:
          onIsDoneEvent(payload.eventPayload, true);
          break;
        case ChatFiltersEventReasonTypes.MARK_READ:
          onMarkDoneEvent(payload.eventPayload);
          break;
        case ChatFiltersEventReasonTypes.CHAT_SLA_STATUS:
          onChatSlaStatusEventForAddChat(payload.eventPayload);
          break;
        case ChatFiltersEventReasonTypes.ASSIGN_TAGS:
          assignTagsEventHandler(payload.eventPayload);
          break;
        default:
          break;
      }
    },
    [
      onNewMessageForAddChatSocketEventHandler,
      onIsDoneEvent,
      onMarkDoneEvent,
      onChatSlaStatusEvent,
      assignTagsEventHandler,
      isAiAwaitingReplyEnabled,
      status,
    ]
  );

  const onChatFiltersRemoveChatEventHandler = useCallback(
    (payload: ChatFiltersEventPayload) => {
      setSocketEventReceived(true);
      switch (payload.eventReason) {
        case ChatFiltersEventReasonTypes.NEW_MESSAGE:
          removeChat(payload.eventPayload.chatId, payload.eventPayload.validUserIds);
          break;
        case ChatFiltersEventReasonTypes.IS_DONE:
          removeChat(payload.eventPayload.chatId, payload.eventPayload.validUserIds);
          break;
        case ChatFiltersEventReasonTypes.MARK_READ:
          removeChat(payload.eventPayload.chatId, payload.eventPayload.validUserIds);
          break;
        case ChatFiltersEventReasonTypes.CHAT_SLA_STATUS:
          removeChat(payload.eventPayload.chatId, payload.eventPayload.validUserIds);
          break;
        case ChatFiltersEventReasonTypes.ASSIGN_TAGS:
          removeChat(payload.eventPayload.chatId, payload.eventPayload.validUserIds);
          break;
        default:
          break;
      }
    },
    [removeChat]
  );

  const onUpdateMessageEventHandler = useCallback(
    (payload: UpdateNoteMessageEventPayload) => {
      setSocketEventReceived(true);
      const message: Message = {
        id: payload.messageId,
        message: payload.message as SystemMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: undefined,
        messageTime: payload.messageTime,
        senderId: payload.senderId,
        messageMetadata: null,
        messageOriginType: MessageOrigin.USER,
        readCount: 0,
        senderUser: payload.chat.senderUser,
        templateId: null,
        totalCount: 1,
        errorMessage: '',
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
      };

      updateCache({
        chatId: payload.chatId,
        message,
        partial: false,
        queue: payload.senderType === MessageOrigin.USER,
        fromSocket: true,
      });
      setInternalNotesList((prev) => {
        const previousNotesList = prev.data;
        const changedListId = prev.data?.find((note) => note.id === payload.messageId)?.id ?? '';

        return {
          ...prev,
          data: previousNotesList?.map((note) =>
            note.id === changedListId
              ? {
                  ...note,
                  note: payload.message.text,
                }
              : note
          ),
        };
      });
    },
    [setInternalNotesList, updateCache]
  );

  const onDeleteMessageEventHandler = useCallback(
    (payload: UpdateNoteMessageEventPayload) => {
      setSocketEventReceived(true);
      deleteCache({
        chatId: payload.chatId,
        messageId: payload.messageId,
      });
      setInternalNotesList((prev) => {
        const previousNotesList = prev.data;
        const changedListId = prev.data?.find((note) => note.id === payload.messageId)?.id ?? '';
        setInternalNotesCount((prev) => Math.max((prev ?? 0) - 1, 0));
        return {
          ...prev,
          data: previousNotesList?.filter((note) => note.id !== changedListId),
        };
      });
    },
    [deleteCache, setInternalNotesList, setInternalNotesCount]
  );

  const onNewNoteMessageEventHandler = useCallback(
    (payload: NewMessageChatRow) => {
      setSocketEventReceived(true);
      const noteText = (payload.message as NoteMessage).text ?? '';
      const mentionedUserIds = Array.from(noteText.matchAll(mentionRegex)).map((match) => match[1]);
      const isCurrentUserMentioned = !!currentUserId && mentionedUserIds.includes(currentUserId);

      if (payload.validUserIds?.length && (!currentUserId || !payload.validUserIds.includes(currentUserId))) {
        return;
      }

      const permissions = getConversationPermissions(payload.chat.id, payload.chat.assignedUserId);

      //FIXME PERMISSION
      if (!permissions.canRead) {
        return;
      }

      if (mentionedUserIds.length && isCurrentUserMentioned && selectedChat?.id !== payload.chatId) {
        const title =
          payload.chat.chatType !== ChatType.INDIVIDUAL
            ? payload.chat.name
            : payload.chat.name || payload.chat.phoneNumber;
        showPushNotification({
          messageId: payload.messageId,
          collapseKey: '',
          from: '',
          data: {
            chatId: payload.chatId,
            chatString: JSON.stringify(payload.chat),
            type: FCMNotificationType.CUSTOMER_NOTE,
            title: title,
            body:
              formatNoteMentions(noteText, teamMembersPrivateNotesData?.data?.allTeamMembers) || 'You have a new note',
            orgId: payload.orgId,
            messageId: payload.messageId,
            senderType: payload.senderType,
          },
        });
      }

      const message: Message = {
        id: payload.messageId,
        message: payload.message as SystemMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: payload.replyMessage ?? undefined,
        messageTime: payload.messageTime,
        senderId: payload.senderId,
        messageOriginType: payload.messageOriginType,
        messageMetadata: payload.messageMetadata,
        readCount: 0,
        senderUser: payload.chat.senderUser,
        templateId: null,
        totalCount: 1,
        errorMessage: '',
        unreadCount: 0,
        noResponseCount: 0,
        showReadMore: !(selectedChat?.id === payload.chatId && payload.messageOriginType === MessageOrigin.CUSTOMER),
        awaitingTemplateApprovalCount: 0,
      };

      if (payload.message.messageType === MessageTypesEnum.NOTE && selectedChat?.id === payload.chatId) {
        setInternalNotesList((prev) => {
          const previousNotesList = prev.data;
          const isNoteAlreadyPresent = previousNotesList?.find((note) => note.id === payload.messageId);

          if (isNoteAlreadyPresent) return prev;
          setInternalNotesCount((prev) => (prev ?? 0) + 1);
          return {
            ...prev,
            data: [
              {
                id: payload.messageId,
                createdBy: payload.chat.senderUser?.name,
                updatedBy: '',
                dateCreated: String(new Date(payload.messageTime)),
                dateUpdated: '',
                note: (payload.message as NoteMessage).text,
              } as InternalNote,
              ...(previousNotesList ?? []),
            ],
          };
        });
      }

      updateCache({
        chatId: payload.chatId,
        message,
        partial: false,
        queue: payload.senderType === MessageOrigin.USER,
        fromSocket: true,
      });
    },
    [
      currentUserId,
      getConversationPermissions,
      selectedChat?.id,
      setInternalNotesCount,
      setInternalNotesList,
      setSocketEventReceived,
      showPushNotification,
      updateCache,
      teamMembersPrivateNotesData?.data?.allTeamMembers,
    ]
  );

  const onLastMessageStatusAddChatEventHandler = useCallback(
    (payload: MessageStatus) => {
      setSocketEventReceived(true);
      const { chatId, errorMessage, status } = payload;
      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        data: {
          id: chatId,
          ...payload,
          lastMessage: {
            id: payload.messageId,
            senderId: payload.senderId,
            message: payload.message,
            messageOriginType: payload.senderType,
            messageTime: payload.messageTime,
            ...getCountFromStatus(status, errorMessage),
          },
        },
        partial: true,
        reOrder: false,
      });
      if (
        payload.message.messageType === MessageTypesEnum.TEXT &&
        (payload.message.mediaDisable === true || payload.message.textDisable === true)
      ) {
        updateCache({
          chatId: payload.chatId,
          message: { id: payload.messageId, message: { ...payload.message, text: payload.message.text ?? '' } },
          partial: true,
        });
      }
    },
    [setChatRow, updateCache]
  );

  const onReminderCreatedEventHandler = useCallback((payload: ReminderCreatedPayload) => {
    const message: Message = {
      id: payload.chatMessageId,
      message: {
        reminderId: payload.reminderId,
        messageType: MessageTypesEnum.REMINDER,
        reminderStatus: payload.reminderStatus,
        reminderMessage: payload.reminderMessage,
        assigneeDetails: payload.assigneeDetails,
        dueAt: payload.dueAt,
        createdBy: payload.createdBy,
      } as ReminderMessage,
      repliesCount: 0,
      deliveryCount: 0,
      erroredCount: 0,
      sentCount: 0,
      replyMessage: undefined,
      messageTime: 0,
      senderId: '',
      messageOriginType: MessageOrigin.SYSTEM,
      messageMetadata: null,
      readCount: 0,
      senderUser: undefined,
      templateId: null,
      totalCount: 1,
      unreadCount: 0,
      noResponseCount: 0,
      awaitingTemplateApprovalCount: 0,
      errorMessage: '',
    };

    updateCache({
      chatId: payload.chatId,
      message,
      partial: false,
      queue: false,
      fromSocket: true,
    });
  }, []);

  const onReminderTriggeredEventHandler = useCallback((payload: ReminderTriggeredPayload) => {
    const reminderMessageToCache: Message = {
      id: payload.chatMessageId || '',
      message: {
        reminderId: payload.reminderId,
        messageType: MessageTypesEnum.REMINDER,
        reminderStatus: REMINDER_MESSAGE_STATUS.TRIGGERED,
        reminderMessage: payload.reminderMessage,
        assigneeDetails: payload.assigneeDetails,
        dueAt: payload.dueAt,
      } as ReminderMessage,
      repliesCount: 0,
      deliveryCount: 0,
      erroredCount: 0,
      sentCount: 0,
      replyMessage: undefined,
      messageTime: 0,
      senderId: '',
      messageOriginType: MessageOrigin.SYSTEM,
      messageMetadata: null,
      readCount: 0,
      senderUser: undefined,
      templateId: null,
      totalCount: 1,
      unreadCount: 0,
      noResponseCount: 0,
      awaitingTemplateApprovalCount: 0,
      errorMessage: '',
    };

    const reminderTriggerMessage: Message = {
      id: payload.reminderTriggerMessageId || '',
      message: {
        reminderId: payload.reminderId,
        messageType: MessageTypesEnum.REMINDER_TRIGGER,
        reminderStatus: REMINDER_MESSAGE_STATUS.TRIGGERED,
        reminderMessage: payload.reminderMessage,
        assigneeDetails: payload.assigneeDetails,
        dueAt: payload.dueAt,
      } as ReminderTriggerMessage,
      repliesCount: 0,
      deliveryCount: 0,
      erroredCount: 0,
      sentCount: 0,
      replyMessage: undefined,
      messageTime: 0,
      senderId: '',
      messageOriginType: MessageOrigin.SYSTEM,
      messageMetadata: null,
      readCount: 0,
      senderUser: undefined,
      templateId: null,
      totalCount: 1,
      unreadCount: 0,
      noResponseCount: 0,
      awaitingTemplateApprovalCount: 0,
      errorMessage: '',
    };

    updateCache({
      chatId: payload.chatId || '',
      message: reminderMessageToCache,
      partial: false,
      queue: false,
      fromSocket: true,
    });

    updateCache({
      chatId: payload.chatId || '',
      message: reminderTriggerMessage,
      partial: false,
      queue: false,
      fromSocket: true,
    });

    setReminderTrigger(() => ({
      open: true,
      reminderId: payload.reminderId,
      chatId: payload.chatId,
      reminderMessage: payload.reminderMessage,
      dueAt: payload.dueAt,
      assigneeDetails: payload.assigneeDetails,
      chatMessageId: payload.chatMessageId,
      createdBy: payload.createdBy,
      createdByName: payload.createdByName,
      phoneNumber: payload.phoneNumber,
      wabaNumber: payload.wabaNumber,
      chatTypeId: payload.chat.chatTypeId,
      chatName: payload.chat?.name || '',
    }));
  }, []);

  const onReminderStatusUpdatesEventHandler = useCallback(
    (payload: ReminderStatusUpdatePayload) => {
      const reminderMessageToCache: Message = {
        id: payload.reminderMessageId || '',
        message: {
          reminderId: payload.reminderId,
          messageType: MessageTypesEnum.REMINDER,
          reminderStatus: payload.reminderStatus,
          reminderMessage: payload.reminderMessage,
          assigneeDetails: payload.assigneeDetails,
          dueAt: payload.dueAt,
          createdBy: payload.createdBy,
        } as ReminderMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: undefined,
        messageTime: 0,
        senderId: '',
        messageOriginType: MessageOrigin.SYSTEM,
        messageMetadata: null,
        readCount: 0,
        senderUser: undefined,
        templateId: null,
        totalCount: 1,
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
        errorMessage: '',
      };

      const reminderTriggerMessage: Message = {
        id: payload.reminderTriggerMessageId || '',
        message: {
          reminderId: payload.reminderId,
          messageType: MessageTypesEnum.REMINDER_TRIGGER,
          reminderStatus: payload.reminderStatus,
          reminderMessage: payload.reminderMessage,
          assigneeDetails: payload.assigneeDetails,
          dueAt: payload.dueAt,
        } as ReminderTriggerMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: undefined,
        messageTime: 0,
        senderId: '',
        messageOriginType: MessageOrigin.SYSTEM,
        messageMetadata: null,
        readCount: 0,
        senderUser: undefined,
        templateId: null,
        totalCount: 1,
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
        errorMessage: '',
      };

      updateCache({
        chatId: payload.chatId || '',
        message: reminderMessageToCache,
        partial: false,
        queue: false,
        fromSocket: true,
      });

      updateCache({
        chatId: payload.chatId || '',
        message: reminderTriggerMessage,
        partial: false,
        queue: false,
        fromSocket: true,
      });

      if (
        reminderTrigger.reminderId === payload.reminderId &&
        payload.reminderStatus === REMINDER_MESSAGE_STATUS.DISMISSED
      ) {
        setReminderTrigger((prev) => ({
          ...prev,
          open: false,
        }));
      }

      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        data: {
          id: payload.chatId,
          isReminderTriggered: payload.isReminderTriggered,
        },
        reOrder: false,
      });
    },
    [reminderTrigger]
  );

  const onAICallEventHandler = useCallback(
    (payload: AICallEventPayload) => {
      setSocketEventReceived(true);
      const { eventReason, eventPayload } = payload;
      const { chatId, chatMessageId } = eventPayload as any;

      if (eventReason === CallSummaryEventReasonTypes.BULK_SUMMARY) {
        const bulkPayload = eventPayload as {
          chatId: string;
          orgId: string;
          chatSummaryWeekId?: string;
          progress: number;
          status: CallSummaryProgressStatus;
          state: CallSummaryProgressState;
          processedCount: number;
          totalCalls: number;
        };

        setCallBulkSummaryProgress((prev) => {
          const existingForChat = prev[bulkPayload.chatId] || {};
          const weekKey = bulkPayload.chatSummaryWeekId || 'default';

          return {
            ...prev,
            [bulkPayload.chatId]: {
              ...existingForChat,
              [weekKey]: {
                chatId: bulkPayload.chatId,
                chatSummaryWeekId: bulkPayload.chatSummaryWeekId,
                progress: bulkPayload.progress,
                status: bulkPayload.status,
                state: bulkPayload.state,
                processedCount: bulkPayload.processedCount,
                totalCalls: bulkPayload.totalCalls,
              },
            },
          };
        });

        return;
      }
      setConversationSocketEvent({
        event: 'ai-call-text-generated',
        eventPayload: { chatMessageId, eventReason },
      });

      const messageAtom = ChatCache.getInstance().getMessageAtom(chatMessageId);
      if (!messageAtom) {
        return;
      }

      const message = ChatCache.getInstance().getAtomValue(messageAtom);
      if (message.message?.messageType !== MessageTypesEnum.CALL) {
        return;
      }

      const callMessage = message.message as CallMessage;

      if (eventReason === CallSummaryEventReasonTypes.SUMMARY) {
        const updatedMessage: Message = {
          ...message,
          message: {
            ...callMessage,
            summary: {
              quickRecap: eventPayload.quickRecap,
              errorMessage: '',
              status: CallSummaryStatus.SUMMARY_COMPLETED,
            },
          },
        };

        updateCache({
          chatId,
          message: updatedMessage,
          partial: false,
          fromSocket: true,
        });
      } else if (eventReason === CallSummaryEventReasonTypes.TRANSCRIPT) {
        const updatedMessage: Message = {
          ...message,
          message: {
            ...callMessage,
            transcription: {
              preview: eventPayload.preview,
              errorMessage: '',
              status: eventPayload.preview
                ? CallSummaryStatus.TRANSCRIPT_COMPLETED
                : CallSummaryStatus.TRANSCRIPT_NOT_FOUND,
            },
            ...(!eventPayload.preview && {
              summary: {
                quickRecap: eventPayload.preview,
                errorMessage: '',
                status: CallSummaryStatus.SUMMARY_NOT_POSSIBLE,
              },
            }),
          },
        };

        updateCache({
          chatId,
          message: updatedMessage,
          partial: false,
          fromSocket: true,
        });
      }
    },
    [setSocketEventReceived, updateCache]
  );

  const onScheduledMessageCreated = useCallback(
    (payload: ScheduledChatMessageSocketPayload) => {
      if (payload.chatId !== selectedChat?.id) return;
      const newMessage: ScheduledMessage = {
        chatMessageId: payload.chatMessageId,
        chatId: payload.chatId,
        status: payload.status,
        scheduledTime: payload.scheduledTime,
        dateUpdated: payload.dateUpdated,
        timezone: payload.upcomingScheduledChatMessageTimezone,
        failedReason: payload.failedReason ?? null,
        message: payload.message,
        createdBy: { id: payload.createdBy?.userId || '', name: payload.createdBy?.name || '' },
      };
      setScheduledMessages((prev) => {
        if (prev.some((m) => m.chatMessageId === payload.chatMessageId)) return prev;
        const updated = [...prev, newMessage];
        if (scheduledSortOption === 'dateUpdated') {
          return updated.sort(
            (a, b) => new Date(b.dateUpdated ?? 0).getTime() - new Date(a.dateUpdated ?? 0).getTime()
          );
        }
        return updated.sort((a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime());
      });
      setCustomerDetails((prev) => {
        if (!prev.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            scheduledChatMessagesCount: (prev.data.scheduledChatMessagesCount ?? 0) + 1,
          },
        };
      });
      const rawTime = payload.upcomingScheduledChatMessageTime;
      const timestamp = rawTime ? new Date(rawTime).getTime() : null;
      setScheduledBanner(timestamp ? { timestamp, timezone: payload.upcomingScheduledChatMessageTimezone } : null);
    },
    [selectedChat?.id, setScheduledMessages, setScheduledBanner, scheduledSortOption, setCustomerDetails]
  );

  const onScheduledMessageUpdated = useCallback(
    (payload: ScheduledChatMessageSocketPayload) => {
      if (payload.chatId !== selectedChat?.id) return;
      setScheduledMessages((prev) => {
        const updated = prev.map((m) =>
          m.chatMessageId === payload.chatMessageId
            ? {
                ...m,
                scheduledTime: payload.scheduledTime,
                dateUpdated: payload.dateUpdated,
                status: payload.status,
                message: payload.message,
              }
            : m
        );
        if (scheduledSortOption === 'dateUpdated') {
          return updated.sort(
            (a, b) => new Date(b.dateUpdated ?? 0).getTime() - new Date(a.dateUpdated ?? 0).getTime()
          );
        }
        return updated;
      });
      const rawTime = payload.upcomingScheduledChatMessageTime;
      const timestamp = rawTime ? new Date(rawTime).getTime() : null;
      setScheduledBanner(timestamp ? { timestamp, timezone: payload.upcomingScheduledChatMessageTimezone } : null);
    },
    [selectedChat?.id, setScheduledMessages, setScheduledBanner, scheduledSortOption]
  );

  const onScheduledMessageDeleted = useCallback(
    (payload: ScheduledChatMessageSocketPayload) => {
      if (payload.chatId !== selectedChat?.id) return;
      setScheduledMessages((prev) => prev.filter((m) => m.chatMessageId !== payload.chatMessageId));
      setCustomerDetails((prev) => {
        if (!prev.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            scheduledChatMessagesCount: Math.max(0, (prev.data.scheduledChatMessagesCount ?? 0) - 1),
          },
        };
      });
      const rawTime = payload.upcomingScheduledChatMessageTime;
      const timestamp = rawTime ? new Date(rawTime).getTime() : null;
      setScheduledBanner(timestamp ? { timestamp, timezone: payload.upcomingScheduledChatMessageTimezone } : null);
    },
    [selectedChat?.id, setScheduledMessages, setScheduledBanner, setCustomerDetails]
  );

  const onScheduledMessageSent = useCallback(
    (payload: ScheduledChatMessageSocketPayload) => {
      if (payload.chatId !== selectedChat?.id) return;
      setScheduledMessages((prev) => prev.filter((m) => m.chatMessageId !== payload.chatMessageId));
      setCustomerDetails((prev) => {
        if (!prev.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            scheduledChatMessagesCount: Math.max(0, (prev.data.scheduledChatMessagesCount ?? 0) - 1),
          },
        };
      });
      const rawTime = payload.upcomingScheduledChatMessageTime;
      const timestamp = rawTime ? new Date(rawTime).getTime() : null;
      setScheduledBanner(timestamp ? { timestamp, timezone: payload.upcomingScheduledChatMessageTimezone } : null);
    },
    [selectedChat?.id, setScheduledMessages, setScheduledBanner, setCustomerDetails]
  );

  useEffect(() => {
    /**
     * This event handler adds new messages to the particular chat's message list
     */
    conversationSocket.on('new-message', onNewMessageSocketEventHandler);
    conversationSocket.on('new-message-v3', onNewMessageSocketEventHandler); // for wa-groups

    /**
     * This event handler updates the SLA related data coming from socket for specific chats
     */
    conversationSocket.on('chat-sla-status', onChatSlaStatusEvent);

    conversationSocket.on('privacy-update', onPrivacyUpdateEvent);

    /**
     * This event handler updates customer blocking and unblocking
     */
    conversationSocket.on('customer-updates', onCustomerUpdatesEvent);

    /**
     * This event handler adds new Chat row to existing Chat list
     */
    conversationSocket.on('chat-filters-add-chat', onChatFiltersAddChatEventHandler);

    /**
     * This event handler removes any existing Chat row from Chat list
     */
    conversationSocket.on('chat-filters-remove-chat', onChatFiltersRemoveChatEventHandler);

    /**
     * This event handler updates old messages, right now only applicable for notes
     */
    conversationSocket.on('update-message', onUpdateMessageEventHandler);

    /**
     * This event handler deletes messages, right now only applicable for notes
     */
    conversationSocket.on('delete-message', onDeleteMessageEventHandler);

    /**
     * This event handler is for new note messages
     */
    conversationSocket.on('new-note-message', onNewNoteMessageEventHandler);

    conversationSocket.on('is-done', onIsDoneEvent);

    /**
     * This event handler adds new Chat row to existing Chat list
     */
    conversationSocket.on('last-message-status', onLastMessageStatusAddChatEventHandler);

    /**
     * These event handlers are for handling events related to reminders
     */
    conversationSocket.on('reminder-created', onReminderCreatedEventHandler);
    conversationSocket.on('reminder-edited', onReminderCreatedEventHandler);
    conversationSocket.on('reminder-triggered', onReminderTriggeredEventHandler);
    conversationSocket.on('reminder-status-updates', onReminderStatusUpdatesEventHandler);

    /**
     * This event handler updates call summary or trascription is ready
     */
    conversationSocket.on('ai-call-text-generated', onAICallEventHandler);

    conversationSocket.on('scheduled-chat-message-created', onScheduledMessageCreated);
    conversationSocket.on('scheduled-chat-message-updated', onScheduledMessageUpdated);
    conversationSocket.on('scheduled-chat-message-deleted', onScheduledMessageDeleted);
    conversationSocket.on('scheduled-chat-message-sent', onScheduledMessageSent);

    return () => {
      conversationSocket.off('new-message', onNewMessageSocketEventHandler);
      conversationSocket.off('new-message-v3', onNewMessageSocketEventHandler); // for wa-groups
      conversationSocket.off('chat-sla-status', onChatSlaStatusEvent);
      conversationSocket.off('privacy-update', onPrivacyUpdateEvent);
      conversationSocket.off('customer-updates', onCustomerUpdatesEvent);
      conversationSocket.off('chat-filters-add-chat', onChatFiltersAddChatEventHandler);
      conversationSocket.off('chat-filters-remove-chat', onChatFiltersRemoveChatEventHandler);
      conversationSocket.off('is-done', onIsDoneEvent);
      conversationSocket.off('update-message', onUpdateMessageEventHandler);
      conversationSocket.off('delete-message', onDeleteMessageEventHandler);
      conversationSocket.off('new-note-message', onNewNoteMessageEventHandler);
      conversationSocket.off('last-message-status', onLastMessageStatusAddChatEventHandler);
      conversationSocket.off('reminder-created', onReminderCreatedEventHandler);
      conversationSocket.off('reminder-edited', onReminderCreatedEventHandler);
      conversationSocket.off('reminder-triggered', onReminderTriggeredEventHandler);
      conversationSocket.off('reminder-status-updates', onReminderStatusUpdatesEventHandler);
      conversationSocket.off('ai-call-text-generated', onAICallEventHandler);
      conversationSocket.off('scheduled-chat-message-created', onScheduledMessageCreated);
      conversationSocket.off('scheduled-chat-message-updated', onScheduledMessageUpdated);
      conversationSocket.off('scheduled-chat-message-deleted', onScheduledMessageDeleted);
      conversationSocket.off('scheduled-chat-message-sent', onScheduledMessageSent);
    };
  }, [
    assignTagsEventHandler,
    conversationSocket,
    onNewMessageSocketEventHandler,
    onPrivacyUpdateEvent,
    onCustomerUpdatesEvent,
    onChatFilterCountsEvent,
    onChatFiltersAddChatEventHandler,
    onChatFiltersRemoveChatEventHandler,
    onLastMessageStatusAddChatEventHandler,
    onChatSlaStatusEvent,
    onDeleteMessageEventHandler,
    onIsDoneEvent,
    onNewNoteMessageEventHandler,
    onReminderCreatedEventHandler,
    onReminderStatusUpdatesEventHandler,
    onReminderTriggeredEventHandler,
    onUpdateMessageEventHandler,
    onAICallEventHandler,
    onScheduledMessageCreated,
    onScheduledMessageUpdated,
    onScheduledMessageDeleted,
    onScheduledMessageSent,
  ]);
};
